import type {
  ComputedRef,
  MaybeRefOrGetter,
  Ref,
} from 'vue';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';

export type Breakpoints<K extends string = string> = Record<
  K,
  MaybeRefOrGetter<number | string>
>;

export type UseElementBreakpointsReturn<K extends string> = {
  greater: (key: K) => ComputedRef<boolean>;
  greaterOrEqual: (key: K) => ComputedRef<boolean>;
  smaller: (key: K) => ComputedRef<boolean>;
  smallerOrEqual: (key: K) => ComputedRef<boolean>;
  between: (a: K, b: K) => ComputedRef<boolean>;
  isGreater: (key: K) => boolean;
  isGreaterOrEqual: (key: K) => boolean;
  isSmaller: (key: K) => boolean;
  isSmallerOrEqual: (key: K) => boolean;
  isInBetween: (a: K, b: K) => boolean;
  current: () => ComputedRef<string[]>;
} & Record<K, ComputedRef<boolean>>;

export function useElementBreakpoints<K extends string>(
  elementRef: Ref<HTMLElement | null>,
  breakpoints: Breakpoints<K>,
): UseElementBreakpointsReturn<K> {
  const elementWidth = ref(0);
  const breakpointStates: Record<K, ComputedRef<boolean>> = {} as Record<
    K,
    ComputedRef<boolean>
  >;

  const updateWidth = (): void => {
    elementWidth.value = elementRef.value ? elementRef.value.clientWidth : 0;
  };

  const getBreakpoint = (key: K): number => {
    if (typeof breakpoints[key] !== 'number') {
      throw new TypeError('breakpoint is not a number');
    }
    return breakpoints[key] as number;
  };

  Object.entries(breakpoints).forEach(([key], index) => {
    breakpointStates[key as K] = computed(() => {
      const width = getBreakpoint(key as K);
      if (index === 0) {
        return elementWidth.value < width;
      }
      if (index === Object.entries(breakpoints).length - 1) {
        return elementWidth.value >= width;
      }
      const nextBreakpoint = Object.entries(breakpoints)[index + 1]?.[1];

      if (nextBreakpoint === undefined) {
        return elementWidth.value >= width;
      }

      return (
        elementWidth.value >= width
        && elementWidth.value
        < (nextBreakpoint as number)
      );
    });
  });

  let observer: ResizeObserver;

  onMounted(() => {
    observer = new ResizeObserver(updateWidth);
    if (elementRef.value) {
      observer.observe(elementRef.value);
    }
  });

  onUnmounted(() => {
    if (observer) {
      observer.disconnect();
    }
  });

  watch(elementRef, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      if (observer && oldVal) {
        observer.unobserve(oldVal);
      }
      if (newVal) {
        observer.observe(newVal);
      }
    }
  });

  const current = (): ComputedRef<string[]> =>
    computed(() =>
      Object.entries(breakpoints)
        .filter(([key]) => breakpointStates[key as K].value)
        .map(([key]) => key),
    );

  const greater = (key: K): ComputedRef<boolean> =>
    computed(() => elementWidth.value > getBreakpoint(key));
  const greaterOrEqual = (key: K): ComputedRef<boolean> =>
    computed(() => elementWidth.value >= getBreakpoint(key));
  const smaller = (key: K): ComputedRef<boolean> =>
    computed(() => elementWidth.value < getBreakpoint(key));
  const smallerOrEqual = (key: K): ComputedRef<boolean> =>
    computed(() => elementWidth.value <= getBreakpoint(key));
  const between = (a: K, b: K): ComputedRef<boolean> =>
    computed(
      () =>
        elementWidth.value >= getBreakpoint(a)
        && elementWidth.value <= getBreakpoint(b),
    );

  const isGreater = (key: K): boolean => elementWidth.value > getBreakpoint(key);
  const isGreaterOrEqual = (key: K): boolean => elementWidth.value >= getBreakpoint(key);
  const isSmaller = (key: K): boolean => elementWidth.value < getBreakpoint(key);
  const isSmallerOrEqual = (key: K): boolean => elementWidth.value <= getBreakpoint(key);
  const isInBetween = (a: K, b: K): boolean =>
    elementWidth.value >= getBreakpoint(a)
    && elementWidth.value <= getBreakpoint(b);

  return {
    ...breakpointStates,
    greater,
    greaterOrEqual,
    smaller,
    smallerOrEqual,
    between,
    isGreater,
    isGreaterOrEqual,
    isSmaller,
    isSmallerOrEqual,
    isInBetween,
    current,
  };
}
